import { useCallback, useEffect, useMemo, useState } from "react";

import { motion } from "framer-motion";
import { orderBy, sum } from "lodash";
import moment from "moment";
import { Line } from "react-chartjs-2";
import { type ChartJSOrUndefined } from "react-chartjs-2/dist/types";

import { LoadingSlideAtom } from "@components/title-slide-view";
import { Tooltip } from "@components/tooltip-container";
import { useUser } from "@components/user-context";
import {
  type GeneralChartSelectorType,
  useModelInsightsKpiGeneralChartIds,
  useModelInsightsKpiGeneralChartSelector,
  useModelInsightsKpiTimePeriod,
} from "@features/mmm/hooks/mmm-url-state";
import { ModelInsightsTrackedComponent } from "@pages/mmm/amplitude-tracking/components";
import {
  getMMMEventSelectionData,
  MMMTab,
} from "@pages/mmm/amplitude-tracking/mmm-event";
import { useTrackMMMEvent } from "@pages/mmm/amplitude-tracking/use-track-mmm-event";
import { Select } from "@shared/selects";

import {
  KpiType,
  type ModelMetadataKpiFormula,
} from "../../explore-budgets/client-setting/types";
import { getColorForIndexOrPlatform } from "../../performance-tracking/tracking-chart/spend-chart/spend-chart-legend/utils";
import {
  REVENUE_ID,
  ROAS_ID,
  SPEND_ID,
} from "../kpi-breakdowns/incremental-roas-chart/const";
import { type DataColumn } from "../kpi-breakdowns/incremental-spend-chart/const";
import { KpiPeriodSelector } from "../kpi-period-selector";
import { curateHistoricalDataset } from "../model-fit-chart/utils/export-to-csv";

import { chartOptions } from "./chart-options";
import { ChartSplitLegend } from "./chart-split-legend";
import { plugins } from "./plugins";
import { setChartData } from "./plugins/chart-data-manager-plugin";
import { calculateDerivativeKpiMetric } from "./utils";

interface Props {
  isLoading: boolean;
  columns: DataColumn[];
  selectedKpi: ModelMetadataKpiFormula;
  historicalContributionsData: any;
}

export type GraphMetadata = {
  breakdowns: Record<string, string>;
  color: string;
  total: number;
  orderingTotal: number;
};

export const AMPLITUDE_EVENT_SELECT_CHANNEL_INSIGHTS_DATE_RANGE_OPTION =
  "Channel insights: Select date range period";

export function GeneralInsightsGraph({
  isLoading,
  columns,
  selectedKpi,
  historicalContributionsData,
}: Props) {
  const trackMMMEvent = useTrackMMMEvent(MMMTab.MODEL_INSIGHTS);
  const [chartRef, setChartRef] = useState<ChartJSOrUndefined<any>>();
  const { user } = useUser();
  const setChartInstance = useCallback(
    (instance: ChartJSOrUndefined | null) => instance && setChartRef(instance),
    [],
  );
  const [selectValue, setSelectValue] =
    useModelInsightsKpiGeneralChartSelector();
  const [dateRange, setDateRange] = useModelInsightsKpiTimePeriod();

  const [chartIds, setChartIds] = useModelInsightsKpiGeneralChartIds();

  const [datasetVisibility] = useState<Record<string, boolean>>({
    [REVENUE_ID]: true,
    [ROAS_ID]: true,
    [SPEND_ID]: true,
  });

  const data = useMemo(() => {
    const datasets: any[] = [];
    const labels: Set<string> = new Set();
    const metadataDataset: {
      metadata: GraphMetadata;
      id: string;
    }[] = [];
    const isRevenue = selectedKpi.kpi_type === KpiType.REVENUE;
    columns.forEach((column, index) => {
      const breakdowns = column.breakdowns.reduce<Record<string, string>>(
        (acc, curr) => {
          acc[curr.key] = curr.value;
          return acc;
        },
        {},
      );
      const color = getColorForIndexOrPlatform(index, breakdowns);

      const rows = historicalContributionsData[column.column_name];

      const dataToUse: number[] = [];
      let totalSpend = 0;
      let totalKpi = 0;

      const rowsToUse = curateHistoricalDataset(rows, dateRange);
      rowsToUse.forEach((row: any) => {
        let value;

        const promoKpiValue = row.promoData[selectedKpi.kpi_name] ?? 0;
        const kpiValue =
          (row.kpiData[selectedKpi.kpi_name] ?? 0) + promoKpiValue;
        totalSpend += row.spend ?? 0;
        totalKpi += kpiValue;

        if (selectValue === "kpi") {
          value = kpiValue;
        } else if (selectValue === "spend") {
          value = row.spend;
        } else {
          value = calculateDerivativeKpiMetric({
            kpi: kpiValue,
            spend: row.spend,
            isRevenue,
          });
        }
        const startDate = moment(row.date);
        const endDate = moment(startDate).add(6, "days");
        labels.add(
          rowsToUse.length > 10
            ? `${startDate.format("MMM D, YYYY")}`
            : `${startDate.format("MMM D, YYYY")} - ${endDate.format(
                "MMM D, YYYY",
              )}`,
        );
        dataToUse.push(value);
      });

      const metadata = {
        breakdowns,
        color,
        // use spend for ROAS or spend
        orderingTotal:
          selectValue === "roas" || selectValue === "spend"
            ? totalSpend
            : sum(dataToUse),
        total:
          // use spend for ROAS or spend
          selectValue === "roas"
            ? isRevenue
              ? totalKpi / totalSpend
              : totalSpend / totalKpi
            : sum(dataToUse),
      };

      metadataDataset.push({
        metadata,
        id: column.column_name,
      });
      if (!chartIds.includes(column.column_name)) {
        return;
      }

      datasets.push({
        backgroundColor: color,
        borderColor: color,
        data: dataToUse,
        label: column.label,
        type: "line",
        yAxisID: REVENUE_ID,
        id: SPEND_ID,
        tension: 0.1,
        pointRadius: 0,
        order: 2,
        metadata,
      });
    });

    return {
      labels: orderBy(Array.from(labels), "asc"),
      datasets,
      metadataDataset,
    };
  }, [
    selectedKpi.kpi_type,
    selectedKpi.kpi_name,
    columns,
    historicalContributionsData,
    dateRange,
    selectValue,
    chartIds,
  ]);

  useEffect(() => {
    if (data?.metadataDataset?.length && chartIds.length === 0) {
      setChartIds(
        orderBy(data.metadataDataset, "metadata.total", "desc")
          .slice(0, 5)
          .map((i) => i.id),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setChartIds]);

  useEffect(() => {
    setChartData(chartRef, { data, datasetVisibility });
  }, [chartRef, data, datasetVisibility]);

  const options = useMemo(() => {
    const optionsToUse: {
      label: string;
      value: GeneralChartSelectorType;
    }[] = [
      {
        label: selectedKpi.friendly_name,
        value: "kpi",
      },
      {
        label: "Spend",
        value: "spend",
      },
      {
        label:
          selectedKpi.kpi_type === KpiType.REVENUE
            ? "Incremental ROAS"
            : "Incremental CAC",
        value: "roas",
      },
    ];
    return optionsToUse;
  }, [selectedKpi.friendly_name, selectedKpi.kpi_type]);

  if (isLoading) {
    return (
      <motion.div
        className="h-[556px]"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <LoadingSlideAtom />
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="font-bold text-xl flex items-center leading-4">
          Incremental Performance by Channel
          <Tooltip
            content={
              <div className="w-[512px]">
                <p>
                  ‘Incremental Performance and Insights by Channel’ compares by
                  channel Returning DTC Transactions contributions. ‘Incremental
                  Spend vs Returning DTC Transactions’ shows how single channel
                  Returning DTC Transactions respond at different levels of
                  spend. ‘Incremental Returning DTC Transactions Over Time’
                  helps answer the question: ‘what if I had spent one more
                  dollar?’ in the preceding weeks. It is computed on your entire
                  data set and displayed for the proceeding weeks.
                </p>
                Learn more about breakdowns in the{" "}
                <a
                  href="https://info.northbeam.io/knowledge/get-started-with-mmm"
                  target="_blank"
                  rel="noreferrer"
                >
                  Northbeam Knowledge Base
                </a>
                .
              </div>
            }
          />
        </div>
        <div className="flex items-center space-x-4">
          <Select
            label={<span className="font-bold">Metric (non-cumulative)</span>}
            className="w-[240px] text-sm py-4"
            value={selectValue}
            options={options}
            onChange={(v) => {
              trackMMMEvent({
                component:
                  ModelInsightsTrackedComponent.ALL_CHANNEL_GENERAL_INSIGHTS_GRAPH_METRIC_SELECTOR,
                action: "onSelect",
                data: getMMMEventSelectionData(v),
              });

              setSelectValue(v);
            }}
          />
          <KpiPeriodSelector
            value={dateRange}
            onChange={(value) => {
              trackMMMEvent({
                component:
                  ModelInsightsTrackedComponent.ALL_CHANNEL_GENERAL_INSIGHTS_GRAPH_TIME_PERIOD_SELECTOR,
                action: "onSelect",
                data: getMMMEventSelectionData(value),
              });

              setDateRange(value);
            }}
          />
        </div>
      </div>
      <ChartSplitLegend
        datasets={data.metadataDataset}
        isRevenue={selectedKpi.kpi_type === KpiType.REVENUE}
      />
      <div>
        <Line
          ref={setChartInstance}
          options={chartOptions(user, selectedKpi.kpi_type, selectValue)}
          data={data as any}
          height={80}
          plugins={plugins}
        />
      </div>
    </motion.div>
  );
}
